import React from 'react';
import { Navigate, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Loader2,
  LogOut,
  LayoutDashboard,
  Users,
  CreditCard,
  Settings,
  Shield,
  ShieldAlert,
  Activity,
  HeartPulse,
  Megaphone,
  ShieldCheck,
  CalendarClock,
  Flag,
  Mail,
  MailCheck,
  Sparkles,
  UserCog,
  LifeBuoy,
  Globe,
  TrendingDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import DotboxLogo from '@/components/common/DotboxLogo';
import PortalSwitcher from '@/components/common/PortalSwitcher';

// Import admin pages
import AdminDashboard from '@/components/admin/AdminDashboard';
import SubscribersList from '@/components/admin/SubscribersList';
import PaymentsPage from '@/components/admin/PaymentsPage';
import AuditLogPage from '@/components/admin/AuditLogPage';
import ActivityPage from '@/components/admin/ActivityPage';
import IPRulesPage from '@/components/admin/IPRulesPage';
import EmailTemplatesPage from '@/components/admin/EmailTemplatesPage';
import SystemHealthPage from '@/components/admin/SystemHealthPage';
import BroadcastsPage from '@/components/admin/BroadcastsPage';
import ModerationPage from '@/components/admin/ModerationPage';
import TrialsPage from '@/components/admin/TrialsPage';
import FeatureFlagsPage from '@/components/admin/FeatureFlagsPage';
import EngagementPage from '@/components/admin/EngagementPage';
import EmbedAnalyticsAdminPage from '@/components/admin/EmbedAnalyticsAdminPage';
import TemplateAnalyticsPage from '@/components/admin/TemplateAnalyticsPage';
import SignupAbusePage from '@/components/admin/SignupAbusePage';
import EmailActivityPanel from '@/components/admin/EmailActivityPanel';
import AICreditUsagePanel from '@/components/admin/AICreditUsagePanel';
import BillingAbuseDashboard from '@/components/admin/BillingAbuseDashboard';
import AdminSupportTickets from '@/components/admin/AdminSupportTickets';
import PauseHistoryPage from '@/components/admin/PauseHistoryPage';
import ActivationsPage from '@/components/admin/ActivationsPage';
import BuildInfoBadge from '@/components/admin/BuildInfoBadge';

const AdminLayout = () => {
  const { isAuthenticated, isAdmin, loading, user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A1024]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-red-400 mx-auto mb-4" />
          <p className="text-slate-300">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const sidebarItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin', testId: 'admin-dashboard' },
    { icon: Users, label: 'Subscribers', path: '/admin/subscribers', testId: 'admin-subscribers' },
    { icon: Activity, label: 'Engagement', path: '/admin/engagement', testId: 'admin-engagement' },
    { icon: Globe, label: 'Embeds Analytics', path: '/admin/embed-analytics', testId: 'admin-embed-analytics' },
    { icon: Sparkles, label: 'Template Analytics', path: '/admin/template-analytics', testId: 'admin-template-analytics' },
    { icon: ShieldAlert, label: 'Signup Abuse', path: '/admin/signup-abuse', testId: 'admin-signup-abuse' },
    { icon: MailCheck, label: 'Activations', path: '/admin/activations', testId: 'admin-activations' },
    { icon: CalendarClock, label: 'Trials', path: '/admin/trials', testId: 'admin-trials' },
    { icon: CreditCard, label: 'Payments', path: '/admin/payments', testId: 'admin-payments' },
    { icon: ShieldAlert, label: 'Billing Intel', path: '/admin/billing-abuse', testId: 'admin-billing-abuse' },
    { icon: HeartPulse, label: 'System Health', path: '/admin/system-health', testId: 'admin-system-health' },
    { icon: TrendingDown, label: 'Pause History', path: '/admin/pause-history', testId: 'admin-pause-history' },
    { icon: Mail, label: 'Email Activity', path: '/admin/email-activity', testId: 'admin-email-activity' },
    { icon: LifeBuoy, label: 'Support Tickets', path: '/admin/support-tickets', testId: 'admin-support-tickets-link' },
    { icon: Sparkles, label: 'AI Credits', path: '/admin/ai-credits', testId: 'admin-ai-credits' },
    { icon: Megaphone, label: 'Broadcasts', path: '/admin/broadcasts', testId: 'admin-broadcasts' },
    { icon: ShieldCheck, label: 'Moderation', path: '/admin/moderation', testId: 'admin-moderation' },
    { icon: Shield, label: 'Audit Log', path: '/admin/audit-log', testId: 'admin-audit-log' },
    { icon: Activity, label: 'Activity', path: '/admin/activity', testId: 'admin-activity' },
    { icon: Settings, label: 'IP Rules', path: '/admin/ip-rules', testId: 'admin-ip-rules' },
    { icon: Flag, label: 'Feature Flags', path: '/admin/feature-flags', testId: 'admin-feature-flags' },
    { icon: LayoutDashboard, label: 'Templates', path: '/admin/email-templates', testId: 'admin-templates' },
  ];

  return (
    <div className="min-h-screen bg-[#0A1024] flex flex-col">
      {/* Header */}
      <header className="bg-[#0F1730] shadow-sm border-b border-[#0F1730]/20 z-50">
        <div className="flex items-center justify-between px-6 py-3">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <DotboxLogo className="h-8" showText={true} variant="dark" />
            <div className="text-white text-lg font-semibold">Admin Portal</div>
          </div>

          {/* iter177 — Portal switcher (Admin ↔ User of the same admin account). */}
          <PortalSwitcher side="admin" />

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="relative h-10 w-10 rounded-full text-white hover:bg-[#DC2626]"
                data-testid="admin-user-menu"
              >
                <Avatar className="h-10 w-10">
                  <AvatarImage src={user?.avatar} alt={user?.full_name || user?.email} />
                  <AvatarFallback className="bg-[#DC2626] text-white">
                    {user?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase() || 
                     user?.email?.charAt(0).toUpperCase() || 'A'}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
              <div className="flex items-center justify-start gap-2 p-2">
                <div className="flex flex-col space-y-1 leading-none">
                  <p className="font-medium">{user?.full_name || 'Admin'}</p>
                  <p className="w-[200px] truncate text-sm text-muted-foreground">
                    {user?.email}
                  </p>
                </div>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigate('/dashboard/command-center')}
                data-testid="admin-switch-to-user-portal"
              >
                <UserCog className="mr-2 h-4 w-4" />
                <span>Switch to User Portal</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate('/dashboard/settings')}
                data-testid="admin-profile-settings-link"
              >
                <Settings className="mr-2 h-4 w-4" />
                <span>Profile & settings</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="admin-logout-button">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Left Sidebar */}
        <TooltipProvider>
          <aside className="bg-[#0F1730] w-64 flex flex-col py-4">
            <nav className="flex-1 px-4">
              <div className="space-y-2">
                {sidebarItems.map(({ icon: Icon, label, path, testId, disabled = false }) => {
                  const isActive = location.pathname === path || (path === '/admin' && location.pathname === '/admin');
                  
                  if (disabled) {
                    return (
                      <div key={path} className="flex items-center px-3 py-2 text-gray-400 cursor-not-allowed">
                        <Icon className="mr-3 h-5 w-5" />
                        <span>{label}</span>
                        <span className="ml-auto text-xs bg-gray-600 text-gray-300 px-2 py-1 rounded-full">
                          Soon
                        </span>
                      </div>
                    );
                  }

                  return (
                    <Button
                      key={path}
                      variant="ghost"
                      className={`w-full justify-start text-white hover:bg-[#DC2626] ${
                        isActive ? 'bg-[#DC2626]' : ''
                      }`}
                      onClick={() => navigate(path)}
                      data-testid={testId}
                    >
                      <Icon className="mr-3 h-5 w-5" />
                      <span>{label}</span>
                    </Button>
                  );
                })}
              </div>
            </nav>

            {/* iter237 — Quiet build-identity readout: which commit is live. */}
            <div className="border-t border-[#1F2A4D] mt-2">
              <BuildInfoBadge />
            </div>
          </aside>
        </TooltipProvider>

        {/* Main Content */}
        <main className="flex-1 overflow-auto">
          <Routes>
            <Route path="/" element={<AdminDashboard />} />
            <Route path="/subscribers" element={<SubscribersList />} />
            <Route path="/engagement" element={<EngagementPage />} />
            <Route path="/embed-analytics" element={<EmbedAnalyticsAdminPage />} />
            <Route path="/template-analytics" element={<TemplateAnalyticsPage />} />
            <Route path="/signup-abuse" element={<SignupAbusePage />} />
            <Route path="/activations" element={<ActivationsPage />} />
            <Route path="/trials" element={<TrialsPage />} />
            <Route path="/payments" element={<PaymentsPage />} />
            <Route path="/billing-abuse" element={<BillingAbuseDashboard />} />
            <Route path="/system-health" element={<SystemHealthPage />} />
            <Route path="/pause-history" element={<PauseHistoryPage />} />
            <Route path="/email-activity" element={<EmailActivityPanel />} />
            <Route path="/support-tickets" element={<AdminSupportTickets />} />
            <Route path="/ai-credits" element={<AICreditUsagePanel />} />
            <Route path="/broadcasts" element={<BroadcastsPage />} />
            <Route path="/moderation" element={<ModerationPage />} />
            <Route path="/audit-log" element={<AuditLogPage />} />
            <Route path="/activity" element={<ActivityPage />} />
            <Route path="/ip-rules" element={<IPRulesPage />} />
            <Route path="/feature-flags" element={<FeatureFlagsPage />} />
            <Route path="/email-templates" element={<EmailTemplatesPage />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;